import { FILTROS_BANDEJA, type CampanaPublic, type FiltroBandeja, type LeadPublic } from '@app/shared';
import { Check, CheckCheck, ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';
import { etiquetaDe, useInstancias, type MapaInstancias } from '../api/instancias.api';
import { formatearTelefono, iniciales, tiempoRelativo } from '../lib/formato';

import { ChipEstado, ChipEtapa, ChipInstancia } from './Chips';

// Orden de la lista (01/10/2026, pedido de Ailén): por fecha el lead al que se le
// acaba de contestar salta al tope y se mezcla con los demás a mitad de una tanda
// de recontactos. Alfabético lo deja fijo en su lugar.
export const ORDENES_BANDEJA = ['fecha_desc', 'fecha_asc', 'alfabetico_asc', 'alfabetico_desc'] as const;
export type OrdenBandeja = (typeof ORDENES_BANDEJA)[number];

// Si se guarda "abierto" o "cerrado" el panel de campaña/etapa/etiqueta/orden, se
// configura una vez y no vuelve a ocupar lugar en cada visita (R6).
const CLAVE_EXPANDIDO = 'bandeja.filtrosExpandidos';
function leerExpandido(): boolean {
  try {
    return localStorage.getItem(CLAVE_EXPANDIDO) === '1';
  } catch {
    return false;
  }
}

interface ListaLeadsProps {
  leads: LeadPublic[];
  conteos: Partial<Record<FiltroBandeja, number>>;
  filtro: FiltroBandeja;
  onFiltro: (f: FiltroBandeja) => void;
  buscar: string;
  onBuscar: (s: string) => void;
  /** Ciclo 1c: filtrar por campaña (id); vacío = todas. */
  campanas: CampanaPublic[];
  campana: string;
  onCampana: (id: string) => void;
  /** Filtro por etapa (1-5), independiente de `filtro`; undefined = todas. */
  etapa: number | undefined;
  onEtapa: (etapa: number | undefined) => void;
  orden: OrdenBandeja;
  onOrden: (o: OrdenBandeja) => void;
  seleccionadoId: string | null;
  onSeleccionar: (id: string) => void;
  cargando: boolean;
  error: boolean;
}

function nombreVisible(lead: LeadPublic, sinNombre: string): string {
  return lead.nombre ?? formatearTelefono(lead.telefono) ?? sinNombre;
}

function fechaDe(lead: LeadPublic): number {
  return new Date(lead.ultimoMensaje?.timestamp ?? lead.creadoEn).getTime();
}

function ordenarLeads(leads: LeadPublic[], orden: OrdenBandeja, sinNombre: string): LeadPublic[] {
  const copia = [...leads];
  switch (orden) {
    case 'fecha_asc':
      return copia.sort((a, b) => fechaDe(a) - fechaDe(b));
    case 'alfabetico_asc':
      return copia.sort((a, b) =>
        nombreVisible(a, sinNombre).localeCompare(nombreVisible(b, sinNombre), 'es', { sensitivity: 'base' }),
      );
    case 'alfabetico_desc':
      return copia.sort((a, b) =>
        nombreVisible(b, sinNombre).localeCompare(nombreVisible(a, sinNombre), 'es', { sensitivity: 'base' }),
      );
    case 'fecha_desc':
    default:
      return copia.sort((a, b) => fechaDe(b) - fechaDe(a));
  }
}

// Columna izquierda de la bandeja: pestañas con conteo, búsqueda y la lista.
export function ListaLeads(p: ListaLeadsProps): JSX.Element {
  const es = useI18n();
  const { porNombre } = useInstancias();
  const [expandido, setExpandido] = useState(leerExpandido);

  const alternarExpandido = (): void => {
    const next = !expandido;
    setExpandido(next);
    try {
      localStorage.setItem(CLAVE_EXPANDIDO, next ? '1' : '0');
    } catch {
      /* sin localStorage */
    }
  };

  const resumen = useMemo(() => {
    const partes: string[] = [];
    const campanaElegida = p.campanas.find((c) => c.id === p.campana);
    if (campanaElegida) partes.push(campanaElegida.nombre);
    if (p.etapa) partes.push(`${es.bandeja.etapa} ${p.etapa}`);
    if (p.filtro !== 'todos') partes.push(es.bandeja.filtros[p.filtro]);
    if (p.orden !== 'fecha_desc') partes.push(es.bandeja.orden[p.orden]);
    return partes.length > 0 ? partes.join(' · ') : es.bandeja.filtrosNinguno;
  }, [p.filtro, p.campana, p.campanas, p.etapa, p.orden, es]);

  const leadsOrdenados = useMemo(
    () => ordenarLeads(p.leads, p.orden, es.bandeja.sinNombre),
    [p.leads, p.orden, es],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border-subtle p-3">
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint"
          />
          <Input
            id="bandeja-buscar"
            value={p.buscar}
            onChange={(e) => p.onBuscar(e.target.value)}
            placeholder={es.bandeja.buscar}
            aria-label={es.bandeja.buscar}
            className="h-9 pl-8 text-sm"
          />
        </div>

        <button
          type="button"
          onClick={alternarExpandido}
          aria-expanded={expandido}
          className="mt-2 flex w-full items-center gap-1.5 text-xs text-text-subtle hover:text-text-strong"
        >
          <ChevronDown size={14} className={cn('shrink-0 transition-transform', expandido && 'rotate-180')} />
          <span className="font-medium">{es.bandeja.filtrosBtn}</span>
          {!expandido && <span className="truncate text-text-faint">{resumen}</span>}
        </button>

        {expandido && (
          <div className="mt-2 flex flex-col gap-2">
            {p.campanas.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-text-subtle">
                <span>{es.bandeja.campana}</span>
                <select
                  value={p.campana}
                  onChange={(e) => p.onCampana(e.target.value)}
                  aria-label={es.bandeja.campana}
                  className="h-7 min-w-0 flex-1 rounded-md border border-border-strong bg-surface px-2 text-xs text-text-strong"
                >
                  <option value="">{es.bandeja.todasLasCampanas}</option>
                  {p.campanas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="flex items-center gap-1.5 text-xs text-text-subtle">
              <span>{es.bandeja.etapa}</span>
              <select
                value={p.etapa ?? ''}
                onChange={(e) => p.onEtapa(e.target.value ? Number(e.target.value) : undefined)}
                aria-label={es.bandeja.etapa}
                className="h-7 min-w-0 flex-1 rounded-md border border-border-strong bg-surface px-2 text-xs text-text-strong"
              >
                <option value="">{es.bandeja.todasLasEtapas}</option>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <option key={n} value={n}>
                    {es.bandeja.etapa} {n}: {es.bandeja.etapas[n]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-xs text-text-subtle">
              <span>{es.bandeja.etiquetas}</span>
              <select
                value={p.filtro}
                onChange={(e) => p.onFiltro(e.target.value as FiltroBandeja)}
                aria-label={es.bandeja.etiquetas}
                className="h-7 min-w-0 flex-1 rounded-md border border-border-strong bg-surface px-2 text-xs text-text-strong"
              >
                {FILTROS_BANDEJA.map((f) => {
                  const n = p.conteos[f];
                  return (
                    <option key={f} value={f}>
                      {es.bandeja.filtros[f]}
                      {typeof n === 'number' && n > 0 ? ` (${n})` : ''}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-xs text-text-subtle">
              <span>{es.bandeja.orden.titulo}</span>
              <select
                value={p.orden}
                onChange={(e) => p.onOrden(e.target.value as OrdenBandeja)}
                aria-label={es.bandeja.orden.titulo}
                className="h-7 min-w-0 flex-1 rounded-md border border-border-strong bg-surface px-2 text-xs text-text-strong"
              >
                {ORDENES_BANDEJA.map((o) => (
                  <option key={o} value={o}>
                    {es.bandeja.orden[o]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto" aria-label={es.bandeja.titulo}>
        {p.cargando && <li className="p-4 text-sm text-text-faint">{es.bandeja.cargando}</li>}
        {p.error && <li className="p-4 text-sm text-danger">{es.bandeja.errorCarga}</li>}
        {!p.cargando && !p.error && leadsOrdenados.length === 0 && (
          <li className="p-6 text-center text-sm text-text-faint">
            {p.buscar ? es.bandeja.sinResultados : es.bandeja.sinLeads}
          </li>
        )}
        {leadsOrdenados.map((lead) => (
          <ItemLead
            key={lead.id}
            lead={lead}
            instancias={porNombre}
            activo={lead.id === p.seleccionadoId}
            onClick={() => p.onSeleccionar(lead.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function ItemLead({
  lead,
  instancias,
  activo,
  onClick,
}: {
  lead: LeadPublic;
  instancias: MapaInstancias;
  activo: boolean;
  onClick: () => void;
}): JSX.Element {
  const es = useI18n();
  const nombre = lead.nombre ?? formatearTelefono(lead.telefono) ?? es.bandeja.sinNombre;
  const u = lead.ultimoMensaje;
  const preview = u ? (u.texto ?? es.bandeja.tipoRecibido[u.tipo === 'TEXTO' ? 'OTRO' : u.tipo]) : '';
  const conNoLeidos = lead.noLeidos > 0;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={activo ? 'true' : undefined}
        className={cn(
          'flex w-full items-start gap-3 border-b border-border-subtle px-3 py-2.5 text-left transition-colors hover:bg-surface-alt',
          activo && 'bg-surface-primario hover:bg-surface-primario',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            conNoLeidos ? 'bg-marca-primario text-white' : 'bg-surface-muted text-text-subtle',
          )}
        >
          {iniciales(lead.nombre, lead.telefono)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                'truncate text-sm',
                conNoLeidos ? 'font-semibold text-text-strong' : 'font-medium text-text-strong',
              )}
            >
              {nombre}
            </span>
            {u && (
              <span className="shrink-0 text-[11px] text-text-faint">{tiempoRelativo(u.timestamp, es)}</span>
            )}
          </span>
          <span className="mt-0.5 flex items-center gap-1 text-xs text-text-subtle">
            {u?.direccion === 'OUT' && (
              <CheckCheck
                size={13}
                className="shrink-0 text-text-faint"
                aria-label={es.bandeja.enviadoPor.OPERADORA}
              />
            )}
            {u?.direccion === 'IN' && conNoLeidos && <Check size={13} className="shrink-0 text-text-marca" />}
            <span className={cn('truncate', conNoLeidos && 'text-text-strong')}>{preview}</span>
            {conNoLeidos && (
              <span
                aria-label={es.bandeja.noLeidos.replace('{n}', String(lead.noLeidos))}
                className="ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-marca-primario px-1.5 text-[11px] font-semibold text-white"
              >
                {lead.noLeidos}
              </span>
            )}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-1">
            {/* Por qué números habló esta persona; la preferida va primero y marcada (R6). */}
            {lead.instancias.map((nombre) => (
              <ChipInstancia
                key={nombre}
                instancia={
                  instancias[nombre] ?? {
                    nombre,
                    etiqueta: etiquetaDe(instancias, nombre),
                    color: '0 0% 45%',
                  }
                }
                preferida={nombre === lead.instanciaPreferida}
              />
            ))}
            <ChipEstado estado={lead.estado} />
            <ChipEtapa etapa={lead.etapa} />
          </span>
        </span>
      </button>
    </li>
  );
}
